<template>
	<button
		type="button"
		class="group flex max-w-[160px] gap-1"
		@click.stop="handleClick"
	>
		<component
			:is="isCopied ? ClipboardDocumentCheckIcon : ClipboardDocumentIcon"
			class="text-accent-500! size-5 shrink-0 transition-opacity"
			:class="iconClasses"
		/>

		<span
			:class="[
				'group-hover:text-accent-600! text-left transition-colors',
				textClasses,
				isCopied ? 'text-accent-600!' : '',
			]"
		>
			{{ displayText }}
		</span>
	</button>
</template>

<script setup lang="ts">
import { ClipboardDocumentIcon } from '@heroicons/vue/24/outline'
import { ClipboardDocumentCheckIcon } from '@heroicons/vue/24/solid'
import { ref, onMounted, computed } from 'vue'

const props = defineProps<{
	displayText: string
	copyRawValue?: string | number
	textClasses?: string
}>()

// Used for mobile two tap behavior
const showIcon = ref(false)

const isCopied = ref(false)
const isMobile = ref(false)

onMounted(() => {
	isMobile.value = window.matchMedia('(hover: none)').matches
})

// Computed for icon opacity
const iconClasses = computed(() => {
	if (isCopied.value || (isMobile.value && showIcon.value)) return 'opacity-100'
	if (isMobile.value && !isCopied.value && !showIcon.value) return 'opacity-0'
	return 'opacity-0 group-hover:opacity-100'
})

/**
 * Handles a click/tap on the component.
 * Desktop: copies the text and toggles the `isCopied` state on click.
 * Mobile: shows the icon on first tap, and copies the text on second.
 */
function handleClick() {
	const copyText = String(props.copyRawValue ?? props.displayText)

	if (!isMobile.value) {
		navigator.clipboard.writeText(copyText)
		isCopied.value = true
		setTimeout(() => (isCopied.value = false), 1000)
		return
	}

	if (!showIcon.value) {
		showIcon.value = true
		setTimeout(() => (showIcon.value = false), 1000)
	} else {
		navigator.clipboard.writeText(copyText)
		isCopied.value = true
		showIcon.value = false
		setTimeout(() => (isCopied.value = false), 1000)
	}
}
</script>
